import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTriangleExclamation, FaXmark } from 'react-icons/fa6';
import { TrainingError } from './types';

interface ErrorSectionProps {
  lastError: TrainingError | null;
  connectionError: string | null;
  onClearError: () => void;
}

export const ErrorSection: React.FC<ErrorSectionProps> = memo(({
  lastError,
  connectionError,
  onClearError
}) => {
  const { t } = useTranslation('training');

  return (
    <>
      {/* Training Error */}
      {lastError && (
        <div className="rounded-xl border border-red-200/60 bg-card shadow-sm overflow-hidden">
          <div className="px-4 py-3 sm:px-5 flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <FaTriangleExclamation className="text-red-500 text-[10px]" />
              </div>
              <div className="min-w-0">
                <span className="text-xs sm:text-sm font-semibold text-red-800 block tracking-tight">
                  {t('postTraining.error.title', 'Training Error')}
                </span>
                <p className="text-[11px] sm:text-xs text-red-600 leading-relaxed mt-0.5">{lastError.message}</p>
                <p className="text-[10px] text-red-400 mt-1">
                  {t('postTraining.error.phase', 'Phase')}: {lastError.phase}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClearError}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-100 transition-all duration-150 flex-shrink-0"
              aria-label={t('postTraining.error.dismiss', 'Dismiss')}
            >
              <FaXmark className="text-xs" />
            </button>
          </div>
        </div>
      )}

      {/* Connection Error */}
      {connectionError && (
        <div className="rounded-xl border border-amber-200/60 bg-card shadow-sm overflow-hidden">
          <div className="px-4 py-3 sm:px-5 flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <FaTriangleExclamation className="text-amber-500 text-[10px]" />
              </div>
              <div className="min-w-0">
                <span className="text-xs sm:text-sm font-semibold text-amber-800 block tracking-tight">
                  {t('postTraining.error.connectionIssue', 'Connection Issue')}
                </span>
                <p className="text-[11px] sm:text-xs text-amber-600 leading-relaxed mt-0.5">{connectionError}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClearError}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-amber-400 hover:text-amber-600 hover:bg-amber-100 transition-all duration-150 flex-shrink-0"
              aria-label={t('postTraining.error.dismiss', 'Dismiss')}
            >
              <FaXmark className="text-xs" />
            </button>
          </div>
        </div>
      )}
    </>
  );
});

ErrorSection.displayName = 'ErrorSection';

export default ErrorSection;
